import { useCallback } from 'react';
import useHash from '../Hooks/UseHash';
import toast from 'react-hot-toast';

interface UserApiProps {
	route: string;
	data?: Record<string, unknown>;
	errorMessage?: string;
	signal?: AbortSignal;
	method?: 'POST' | 'GET';
	baseUrl?: string;
}

interface UserFileUploadProps {
	route: string;
	data: FormData;
	errorMessage?: string;
	signal?: AbortSignal;
	baseUrl?: string;
}

const redirectIfMaintenance = async (res: Response) => {
	if (res.status !== 503) {
		return false;
	}
	try {
		const cloned = res.clone();
		const data = (await cloned.json()) as { maintenance?: boolean };
		if ((data as { maintenance?: boolean })?.maintenance) {
			window.location.assign('/u/maintenance');
			return true;
		}
		// fall through if JSON isn't in expected format
	} catch {
		// ignore parse errors, continue to redirect below
	}
	window.location.assign('/u/maintenance');
	return true;
};

// Core API function that can be used anywhere, not React-dependent
export async function fetchApi<T = undefined>({
	route,
	data,
	errorMessage = 'Error loading data',
	signal,
	method = 'POST',
	baseUrl = '/u/',
}: UserApiProps) {
	const res = await fetch(baseUrl + route, {
		method,
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
		signal,
	});
	if (await redirectIfMaintenance(res)) {
		throw new Error('Goodwill Projects is under maintenance');
	}
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const result: ServerResponse<T> = await res.json();
	if (ErrorHandler.handle(result)) {
		throw new Error(result.message ?? errorMessage);
	}
	return result;
}

// Core file upload function
export async function fetchFileUpload<T = undefined>({
	route,
	data,
	errorMessage = 'Error uploading file',
	signal,
	baseUrl = '/u/',
}: UserFileUploadProps) {
	const res = await fetch(baseUrl + route, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
		},
		body: data,
		signal,
	});
	if (await redirectIfMaintenance(res)) {
		throw new Error('Goodwill Projects is under maintenance');
	}
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const result: ServerResponse<T> = await res.json();
	if (ErrorHandler.handle(result)) {
		throw new Error(result.message ?? errorMessage);
	}
	return result;
}

// Non-React version for use with a specific hash
export function createUserApi(hash: string) {
	return async <T = undefined>(props: UserApiProps) => {
		return fetchApi<T>({
			...props,
			data: {
				...props.data,
				hash,
			},
		});
	};
}

// Non-React file upload version for use with a specific hash
export function createUserFileUpload(hash: string) {
	return async <T = undefined>(props: UserFileUploadProps) => {
		// Add hash to FormData
		const formData = new FormData();
		for (const [key, value] of props.data.entries()) {
			formData.append(key, value);
		}
		formData.append('hash', hash);

		return fetchFileUpload<T>({
			...props,
			data: formData,
		});
	};
}

// React hook version that uses hash from context
export default function UseUserApi() {
	const hash = useHash();
	// We still need useCallback here to properly memoize based on hash
	return useCallback(
		async <T = undefined>(props: UserApiProps) => {
			return createUserApi(hash)<T>(props);
		},
		[hash],
	);
}

// React hook for file uploads using hash from context
export function useUserFileUpload() {
	const hash = useHash();
	return useCallback(
		async <T = undefined>(props: UserFileUploadProps) => {
			return createUserFileUpload(hash)<T>(props);
		},
		[hash],
	);
}

// React hook for use with a specific hash
export function UseUserApiWithHash(hash: string) {
	// No need for useCallback since hash is expected to be stable when provided explicitly
	return createUserApi(hash);
}

// Version that doesn't include a hash at all
export function apiWithoutHash() {
	return fetchApi;
}

type ErrorResponse = {
	success: false;
	message?: string;
};
type BaseSuccessResponse = {
	success: true;
};
type SuccessResponse<T = undefined> = undefined extends T ? BaseSuccessResponse : BaseSuccessResponse & { data: T };
export type ServerResponse<T = undefined> = ErrorResponse | SuccessResponse<T>;

export class ErrorHandler {
	static handle<T = undefined>(res: ServerResponse<T>): res is ErrorResponse {
		if (!res.success) {
			return true;
		}
		return false;
	}
	static toast(e: unknown) {
		if (e instanceof Error) {
			toast.error(e.message);
		}
	}
}
